import React, { useEffect, useState } from 'react';
import toast from 'react-hot-toast';
import { LuCheck, LuLoaderPinwheel, LuMailOpen, LuX } from 'react-icons/lu';
import { useNavigate } from 'react-router-dom';

import { StandaloneInvitation } from '../../entities/standalone-invitation';
import {
  acceptStandaloneInvitation,
  declineStandaloneInvitation,
  listStandaloneInvitations,
  standaloneErrorMessage,
} from '../../shared/api/standalone';
import { invalidateStandaloneProjectsCache } from '../../shared/hooks/use-standalone-projects';

// MF-58 invitations inbox — rendered on both /standalone and the main dashboard, so an invited
// user sees it right after signing in instead of having to find the Standalone tab first.
// Renders nothing while loading, on fetch failure, or when there are no pending invitations.
export const PendingInvitations: React.FC = () => {
  const navigate = useNavigate();
  const [invitations, setInvitations] = useState<StandaloneInvitation[]>([]);
  const [respondingId, setRespondingId] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    listStandaloneInvitations()
      .then((invs) => !cancelled && setInvitations(invs))
      .catch(() => {});
    return () => {
      cancelled = true;
    };
  }, []);

  const handleAccept = async (invitation: StandaloneInvitation) => {
    setRespondingId(invitation.projectId);
    try {
      await acceptStandaloneInvitation(invitation.projectId);
      // The accept-side WS broadcast only reaches the project owner's socket room, not the
      // person who just accepted — refetch our own project list explicitly instead of waiting
      // on a live event that will never arrive for this action.
      await invalidateStandaloneProjectsCache();
      setInvitations((prev) => prev.filter((i) => i.projectId !== invitation.projectId));
      toast.success(`Joined ${invitation.projectName}.`);
      navigate(`/standalone/${invitation.projectId}`);
    } catch (err) {
      toast.error(standaloneErrorMessage(err) ?? 'Failed to accept invitation.');
    } finally {
      setRespondingId(null);
    }
  };

  const handleDecline = async (invitation: StandaloneInvitation) => {
    setRespondingId(invitation.projectId);
    try {
      await declineStandaloneInvitation(invitation.projectId);
      setInvitations((prev) => prev.filter((i) => i.projectId !== invitation.projectId));
    } catch (err) {
      toast.error(standaloneErrorMessage(err) ?? 'Failed to decline invitation.');
    } finally {
      setRespondingId(null);
    }
  };

  if (invitations.length === 0) return null;

  return (
    <div className="card bg-base-100 shadow-card">
      <div className="card-body p-4 gap-2">
        <span className="text-sm font-semibold flex items-center gap-1.5">
          <LuMailOpen size={15} className="opacity-60" />
          Project invitations
        </span>
        <div className="space-y-1.5">
          {invitations.map((invitation) => (
            <div
              key={invitation.projectId}
              className="rounded-lg bg-base-200 px-3 py-2 flex items-center justify-between gap-3"
            >
              <div className="min-w-0">
                <span className="text-sm font-medium truncate block">{invitation.projectName}</span>
                <span className="text-xs opacity-50">Invited as {invitation.role}</span>
              </div>
              <div className="flex items-center gap-1.5 shrink-0">
                <button
                  className="btn btn-ghost btn-xs gap-1"
                  onClick={() => handleDecline(invitation)}
                  disabled={respondingId === invitation.projectId}
                >
                  <LuX size={13} />
                  Decline
                </button>
                <button
                  className="btn btn-primary btn-xs gap-1"
                  onClick={() => handleAccept(invitation)}
                  disabled={respondingId === invitation.projectId}
                >
                  {respondingId === invitation.projectId ? (
                    <LuLoaderPinwheel className="animate-spin" size={13} />
                  ) : (
                    <LuCheck size={13} />
                  )}
                  Accept
                </button>
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

export default PendingInvitations;
